import { JSX, useContext, useMemo, useRef } from "react";
import { WizardStepProps } from "../import/interfaces";
import { VolumeReportsSpecialtiesFilters } from "../import/VolumeReportsSpecialtiesFilters";
import { VolumeReportsCountiesFilters } from "../import/VolumeReportsCountiesFilter";
import { VolumeReportsStatesFilters } from "../import/VolumeReportsStatesFilters";
import { VolumeReportsProvidersFilter } from "../import/VolumeReportsProvidersFilter";
import { RouteComponentProps } from "react-router-dom";
import { VolumeReportsMembershipFilters } from "../import/VolumeReportsMembershipFilters";
import { SummarySpecialtyVolumeReportsCountiesFilters } from "../import/SummarySpecialtyVolumeReportsCountiesFilters";
import { VolumeReportsServiceAreaFilter } from "../import/VolumeReportsServiceAreaFilter";
import { VolumeReportsContext, volumeReportTypeToReportTypeEnumMap } from "../import/VolumeReportsContext";
import FlyoutLink from "@app/shared/Flyouts/FlyoutLink";
import { ColumnConfigurationPanel, PanelMode } from "@app/report-customization/build-reports/components/ColumnConfigurationPanel";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import PageSection from "@app/shared/PageSection";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";

const VolumeReportsFilters = (
  props: WizardStepProps & RouteComponentProps & { isStandardizedReport: boolean; },
): JSX.Element => {
  const wizardContext = useContext(VolumeReportsContext);
  const { enabledFeatureFlags } = useContext(FeatureFlagContext);

  const isProvidersRoute = useRef(
    props.location.pathname.includes("providers"),
  ).current;

  const isCountyAggregate = useRef(
    props.location.pathname.includes("countyaggregate"),
  ).current;

  const isStateAggregate = useRef(
    props.location.pathname.includes("stateaggregate"),
  ).current;
  const isSpecialtySummaryByCounty = useRef(
    props.location.pathname.includes("specialty"),
  ).current;
  const isZipClassAggregate = useRef(
    props.location.pathname.includes("zip-class-aggregate"),
  ).current;
  const usesZipServiceAreaFilter = useRef(
    props.location.pathname.includes("zip-providers") || props.location.pathname.includes("specialty-summary-zip-code"),
  ).current;

  const columnPresets = wizardContext.columnPresets ?? [];

  const presetsOptions = useMemo(() => {
    return columnPresets?.map((p) => <option key={p.id} value={p.id}>{p.name}</option>);
  }, [columnPresets]);

  const privatePresetNames = useMemo(() => {
    return columnPresets?.filter((p) => p.isPrivate).map((p) => p.name);
  }, [columnPresets]);

  return (
    <PageSection>
      <div className="vr__filters-container">
        {isProvidersRoute && <VolumeReportsProvidersFilter {...props} />}
        {!isCountyAggregate && !isStateAggregate && (
          <VolumeReportsSpecialtiesFilters />
        )}
        {isZipClassAggregate && (
          <section>
            <h2 className="header-m">Classification Filter</h2>
            <p>Please select the classification to include:</p>
            <QESSelect
              allowEmpty
              containerClassName="mt-4"
              label="Classification"
              onChange={(e) => wizardContext.setZipClassification(e.target.value)}
              placeholder="All Classifications"
              value={wizardContext.zipClassification}
              disabled={wizardContext.zipClassOptionsLoading}
              testId="volume-reports__zip-classification"
            >
              {[...(wizardContext.zipClassOptions ?? [])].sort((a, b) => a.localeCompare(b)).map((zc) => <option key={zc} value={zc}>{zc}</option>)}
            </QESSelect>
          </section>
        )}
        {isStateAggregate && <VolumeReportsStatesFilters />}
        {!isZipClassAggregate && !usesZipServiceAreaFilter && (isSpecialtySummaryByCounty ? <SummarySpecialtyVolumeReportsCountiesFilters /> : <VolumeReportsCountiesFilters />)}
        {usesZipServiceAreaFilter && <VolumeReportsServiceAreaFilter />}
        {(isSpecialtySummaryByCounty || (isProvidersRoute && enabledFeatureFlags.has(Features.VOLUME_PROVIDER_REPORT_MEMBERSHIP_FILTER))) && props.isStandardizedReport && <VolumeReportsMembershipFilters />}
      </div>

      {enabledFeatureFlags.has(Features.ADEQUACY_REPORT_CUSTOMIZATION) && !!wizardContext.columnPresets && (
        <div className="container mx-0 px-0">
          <div className="row mt-3">
            <h2 className="header-s col-auto">Column Configuration</h2>
          </div>
          <div className="row">
            <QESSelect
              label="Preset"
              containerClassName="col-4"
              value={wizardContext.selectedColumnPreset?.id}
              onChange={(e) => wizardContext.setSelectedColumnPreset(wizardContext.columnPresets.find((p) => p.id.toString() === e.target.value))}
              testId="volume-reports__preset"
            >
              {presetsOptions}
            </QESSelect>
            {wizardContext.selectedColumnPreset.id !== -1 && (
              <div className="align-self-end col-auto px-0">
                <FlyoutLink
                  classNames="qes-button-secondary"
                  testId="report-customization__create-new-preset"
                  content={{
                    body: (
                      <ColumnConfigurationPanel
                        presetId={wizardContext.selectedColumnPreset.id}
                        setHasConfigurationChanges={() => null}
                        panelMode={PanelMode.View}
                        allPrivatePresetNames={privatePresetNames}
                        reportType={volumeReportTypeToReportTypeEnumMap.get(wizardContext.reportType)}
                      />
                    ),
                    title: "Column Configuration",
                  }}
                  size="40"
                >
                  Preview
                </FlyoutLink>
              </div>
            )}
          </div>
        </div>
      )}
    </PageSection>
  );
};

export default VolumeReportsFilters;
